import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Stack,
  Switch,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'

import { CustomForm } from 'components/tools/CustomForm/types'
import { ButtonWithPermission } from 'components/validators'
import CustomFormBuilderScoreClassification from './components/CustomFormBuilderScoreClassification'
import { useCustomFormBuilder } from '../../../../contexts/CustomFormBuilderManagerContext/Provider'

type Props = {
  formMethods: UseFormReturn<CustomForm>
}

export default function CustomFormBuilderDrawer({ formMethods }: Props) {
  const [loadingButton, setLoadingButton] = useState(false)

  const { watch, register, setValue, handleSubmit } = formMethods

  const { isOpen, onClose, onOpen } = useDisclosure()

  const { isFormActionsDisabled, permissions, form, setForm, updateForm } = useCustomFormBuilder()

  const hasClassification = watch('has_classification')

  function formScoreClassificationIntervals(data: any) {
    const updatedIntervals: any[] = []
    data.score_settings.classification.intervals.forEach((interval: any) => {
      const localInterval = { ...interval }
      if (localInterval.condition === 'BIGGER_THAN') delete localInterval.to
      if (localInterval.condition === 'LOWER_THAN') delete localInterval.from
      updatedIntervals.push(localInterval)
    })
    return updatedIntervals
  }

  const onSubmit = async (data: any) => {
    setLoadingButton(true)
    data.score_settings.classification.name = data.name
    if (data.score_settings.classification) {
      const formatedIntervals = formScoreClassificationIntervals(data)
      data.score_settings.classification.intervals = formatedIntervals
    }
    delete data.name
    const response = await updateForm(data)
    if (response?.header?.success) {
      const formFromAPI = response?.body?.form
      onClose()
      setForm({
        ...form,
        has_classification: formFromAPI.has_classification,
        score_settings: formFromAPI.score_settings,
      })
    }
    setLoadingButton(false)
  }

  if (isFormActionsDisabled) {
    return (
      <Tooltip label="Este formulário é protegido, não pode ser alterado / deletado">
        <Box>
          <ButtonWithPermission isDisabled variant="outline" minWidth="100%">
            Configurar formulário
          </ButtonWithPermission>
        </Box>
      </Tooltip>
    )
  }

  return (
    <>
      <ButtonWithPermission
        customDisableCondition={permissions?.updateForm}
        variant="outline"
        minWidth="100%"
        onClick={onOpen}
      >
        Configurar formulário
      </ButtonWithPermission>

      <Drawer isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />

        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Heading>Atualizando formulário</Heading>
          </DrawerHeader>

          <DrawerBody>
            <Stack spacing={4}>
              <FormControl
                display="flex"
                flexDir="column"
                gap={2}
                border="1px solid"
                borderColor="borderColor"
                rounded="md"
                p={2}
                bg="bgContent"
              >
                <Flex justifyContent="space-between" alignItems="center" gap={4}>
                  <Flex flexDir="column">
                    <FormLabel fontWeight="bold">Classificação</FormLabel>
                    <FormHelperText>
                      Caso o formulário possua pontuação, é possível criar uma classificação com intervalos dinâmicos.
                    </FormHelperText>
                  </Flex>
                  <Center w="50px">
                    <Switch maxWidth="max-content" isChecked={hasClassification} {...register('has_classification')} />
                  </Center>
                </Flex>

                {hasClassification && (
                  <>
                    <Divider />
                    <Stack spacing={4}>
                      <CustomFormBuilderScoreClassification register={register} setValue={setValue} watch={watch} />
                    </Stack>
                  </>
                )}
              </FormControl>
            </Stack>
          </DrawerBody>

          <DrawerFooter>
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>

            <Button isLoading={loadingButton} onClick={() => handleSubmit(onSubmit)()}>
              Atualizar
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
